/* ============================================================
   Curseur de prix custom à deux poignées
   - Autonome : fonctionne même sans filtre Shopify natif
   - Émet 'price-range:change' en continu (debounce 100ms) pour filtrage live
   - Soumet le formulaire Shopify pour le mode grille classique
   ============================================================ */

(function () {
  if (typeof window === 'undefined') return;

  const DEBOUNCE_MS = 100;

  class PriceRangeSlider extends HTMLElement {
    connectedCallback() {
      // Bornes
      this.rangeMin = parseFloat(this.dataset.rangeMin || '0');
      this.rangeMax = parseFloat(this.dataset.rangeMax || '0');
      this.currentMin = parseFloat(this.dataset.currentMin || this.rangeMin);
      this.currentMax = parseFloat(this.dataset.currentMax || this.rangeMax);
      this.currencySymbol = this.dataset.currencySymbol || '€';

      // Garde-fous
      if (!this.rangeMax || this.rangeMax <= this.rangeMin) {
        this.rangeMax = this.rangeMin + 100;
      }
      if (!this.currentMax || this.currentMax === 0 || this.currentMax > this.rangeMax) {
        this.currentMax = this.rangeMax;
      }
      if (isNaN(this.currentMin) || this.currentMin < this.rangeMin) {
        this.currentMin = this.rangeMin;
      }

      // Éléments DOM
      this.trackContainer = this.querySelector('[data-track-container]');
      this.trackFill = this.querySelector('[data-track-fill]');
      this.handleMin = this.querySelector('[data-handle="min"]');
      this.handleMax = this.querySelector('[data-handle="max"]');
      this.displayMin = this.querySelector('[data-price-min-display]');
      this.displayMax = this.querySelector('[data-price-max-display]');
      this.inputMin = this.querySelector('[data-price-min-input]');
      this.inputMax = this.querySelector('[data-price-max-input]');

      if (!this.trackContainer || !this.handleMin || !this.handleMax) {
        // DOM incomplet — initialisation annulée
        return;
      }

      this._dragging = null;
      this._debounceTimer = null;
      this._onMove = (e) => this.onMove(e);
      this._onEnd = (e) => this.endDrag(e);

      this.bindEvents();

      // Rendu initial après calcul du layout (sinon getBoundingClientRect peut retourner 0)
      requestAnimationFrame(() => this.render());
    }

    bindEvents() {
      // Pointer events : souris + tactile unifiés
      this.handleMin.addEventListener('pointerdown', (e) => this.startDrag(e, 'min'));
      this.handleMax.addEventListener('pointerdown', (e) => this.startDrag(e, 'max'));

      // Clic sur la barre = déplace la poignée la plus proche
      this.trackContainer.addEventListener('pointerdown', (e) => {
        if (e.target.closest('[data-handle]')) return;
        const ratio = this.ratioFromPointer(e);
        const value = this.rangeMin + ratio * (this.rangeMax - this.rangeMin);
        const distMin = Math.abs(value - this.currentMin);
        const distMax = Math.abs(value - this.currentMax);
        const which = distMin <= distMax ? 'min' : 'max';
        this.setValue(which, value);
        this.startDrag(e, which);
      });

      // Clavier (a11y)
      this.handleMin.addEventListener('keydown', (e) => this.onKey(e, 'min'));
      this.handleMax.addEventListener('keydown', (e) => this.onKey(e, 'max'));
    }

    ratioFromPointer(e) {
      const rect = this.trackContainer.getBoundingClientRect();
      if (!rect.width) return 0;
      const x = e.clientX - rect.left;
      return Math.max(0, Math.min(1, x / rect.width));
    }

    setValue(which, value) {
      if (which === 'min') {
        this.currentMin = Math.max(this.rangeMin, Math.min(value, this.currentMax - 1));
      } else {
        this.currentMax = Math.min(this.rangeMax, Math.max(value, this.currentMin + 1));
      }
      this.render();
      this.scheduleChange();
    }

    startDrag(e, which) {
      e.preventDefault();
      this._dragging = which;
      const handle = which === 'min' ? this.handleMin : this.handleMax;
      handle.setAttribute('data-dragging', 'true');
      try { handle.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
      window.addEventListener('pointermove', this._onMove);
      window.addEventListener('pointerup', this._onEnd);
      window.addEventListener('pointercancel', this._onEnd);
    }

    onMove(e) {
      if (!this._dragging) return;
      const ratio = this.ratioFromPointer(e);
      const value = this.rangeMin + ratio * (this.rangeMax - this.rangeMin);
      this.setValue(this._dragging, value);
    }

    endDrag() {
      if (!this._dragging) return;
      const handle = this._dragging === 'min' ? this.handleMin : this.handleMax;
      handle.removeAttribute('data-dragging');
      this._dragging = null;
      window.removeEventListener('pointermove', this._onMove);
      window.removeEventListener('pointerup', this._onEnd);
      window.removeEventListener('pointercancel', this._onEnd);
      this.commitChange();
    }

    onKey(e, which) {
      const step = (this.rangeMax - this.rangeMin) / 100;
      let changed = false;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
        if (which === 'min') {
          this.currentMin = Math.max(this.rangeMin, this.currentMin - step);
        } else {
          this.currentMax = Math.max(this.currentMin + 1, this.currentMax - step);
        }
        changed = true;
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
        if (which === 'min') {
          this.currentMin = Math.min(this.currentMax - 1, this.currentMin + step);
        } else {
          this.currentMax = Math.min(this.rangeMax, this.currentMax + step);
        }
        changed = true;
      }
      if (changed) {
        e.preventDefault();
        this.render();
        this.commitChange();
      }
    }

    render() {
      const total = this.rangeMax - this.rangeMin || 1;
      const minPct = ((this.currentMin - this.rangeMin) / total) * 100;
      const maxPct = ((this.currentMax - this.rangeMin) / total) * 100;

      this.handleMin.style.left = minPct + '%';
      this.handleMax.style.left = maxPct + '%';
      if (this.trackFill) {
        this.trackFill.style.left = minPct + '%';
        this.trackFill.style.right = (100 - maxPct) + '%';
      }

      if (this.displayMin) this.displayMin.textContent = this.formatMoney(this.currentMin);
      if (this.displayMax) this.displayMax.textContent = this.formatMoney(this.currentMax);

      this.dataset.overlap = (maxPct - minPct) < 15 ? 'true' : 'false';

      this.handleMin.setAttribute('aria-valuenow', String(Math.round(this.currentMin)));
      this.handleMax.setAttribute('aria-valuenow', String(Math.round(this.currentMax)));
    }

    /** Centimes → "€XX,XX" */
    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2).replace('.', ',');
      return this.currencySymbol + amount;
    }

    scheduleChange() {
      this.updateHiddenInputs();
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => {
        this.dispatchEvent(new CustomEvent('price-range:change', {
          bubbles: true,
          detail: { min: this.currentMin, max: this.currentMax }
        }));
      }, DEBOUNCE_MS);
    }

    commitChange() {
      this.updateHiddenInputs();
      this.dispatchEvent(new CustomEvent('price-range:change', {
        bubbles: true,
        detail: { min: this.currentMin, max: this.currentMax }
      }));
      const form = this.closest('form');
      if (form) {
        form.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    updateHiddenInputs() {
      if (this.inputMin) {
        this.inputMin.value = this.currentMin > this.rangeMin ? (this.currentMin / 100).toFixed(2) : '';
      }
      if (this.inputMax) {
        this.inputMax.value = this.currentMax < this.rangeMax ? (this.currentMax / 100).toFixed(2) : '';
      }
    }
  }

  if (!customElements.get('price-range-slider')) {
    customElements.define('price-range-slider', PriceRangeSlider);
  }
})();
